'use es6';

import { useState, useEffect, useCallback } from 'react';
import { OrderedMap, Map as ImmutableMap, fromJS } from 'immutable';
import getIn from 'transmute/getIn';
import I18n from 'I18n';
import formatName from 'I18n/utils/formatName';
import { CONTACT, COMPANY, DEAL, TICKET, ENGAGEMENT } from 'customer-data-objects/constants/ObjectTypes';
import { ObjectTypesToIds, ObjectTypeFromIds, CONTACT_TYPE_ID, COMPANY_TYPE_ID, DEAL_TYPE_ID, TICKET_TYPE_ID, MEETING_EVENT_TYPE_ID, CALL_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import sortBy from 'transmute/sortBy';
import memoize from 'transmute/memoize';
import isLegacyHubSpotObject from 'customer-data-objects/crmObject/isLegacyHubSpotObject';
import isPortalSpecificObjectType from 'customer-data-objects/crmObject/isPortalSpecificObjectType';
import UniversalAssociationRecord from 'universal-associations-select/records/UniversalAssociationRecord';
import UniversalAssociationOptionRecord from 'universal-associations-select/records/UniversalAssociationOptionRecord';
import { useFetchUniversalEngagementAssociations } from 'universal-associations-select/hooks/useFetchUniversalEngagementAssociations';
import { useFetchAutoAssociations } from 'universal-associations-select/hooks/useFetchAutoAssociations';
import { createInMemoryCache } from 'universal-associations-select/helpers/createInMemoryCache';
const associationsCache = createInMemoryCache({
  cacheName: 'associationsCache'
});
const setInAssociationsCache = (key, value) => associationsCache.set(key, value);
const clearAssociationsCache = () => associationsCache.clear();
const HUBSPOT_DEFINED_OBJECT_TYPE_ALLOWLIST = [CONTACT, COMPANY, DEAL, TICKET];
// https://git.hubteam.com/HubSpotProtected/InboundDbAssociations/blob/master/InboundDbAssociationsModels/src/main/java/com/hubspot/inbounddb/associations/base/DefaultAssociationType.java

const HUBSPOT_DEFINED_ASSOCIATION_TYPE_ID_ALLOWLIST = ImmutableMap({
  CALL_TO_COMPANY: 182,
  CALL_TO_CONTACT: 194,
  CALL_TO_DEAL: 206,
  CALL_TO_MEETING: 399,
  CALL_TO_PARTNER_CLIENT: 717,
  CALL_TO_TICKET: 220,
  CALL_TO_ORDER: 773,
  CALL_TO_CART: 775,
  CALL_TO_PARTNER_SERVICE: 823,
  COMMUNICATION_TO_COMPANY: 87,
  COMMUNICATION_TO_CONTACT: 81,
  COMMUNICATION_TO_DEAL: 85,
  COMMUNICATION_TO_TICKET: 83,
  COMMUNICATION_TO_ORDER: 785,
  COMMUNICATION_TO_CART: 787,
  COMPANY_TO_CONTACT_UNLABELED: 280,
  COMPANY_TO_COMPANY: 450,
  COMPANY_TO_CONTACT: 2,
  COMPANY_TO_DEAL_UNLABELED: 342,
  COMPANY_TO_DEAL: 6,
  COMPANY_TO_TICKET_UNLABELED: 340,
  COMPANY_TO_TICKET: 25,
  COMPANY_TO_ORDER: 510,
  COMPANY_TO_CART: 761,
  CONTACT_TO_CONTACT: 449,
  CONTACT_TO_COMPANY_UNLABELED: 279,
  CONTACT_TO_COMPANY: 1,
  CONTACT_TO_DEAL: 4,
  CONTACT_TO_TICKET: 15,
  CONTACT_TO_ORDER: 508,
  CONTACT_TO_CART: 587,
  CONVERSATION_SESSION_TO_COMPANY: 184,
  CONVERSATION_SESSION_TO_CONTACT: 196,
  CONVERSATION_SESSION_TO_DEAL: 208,
  CONVERSATION_SESSION_TO_TICKET: 222,
  CONVERSATION_SESSION_TO_ORDER: 781,
  CONVERSATION_SESSION_TO_CART: 783,
  DEAL_TO_COMPANY_UNLABELED: 341,
  DEAL_TO_COMPANY: 5,
  DEAL_TO_CONTACT: 3,
  DEAL_TO_DEAL: 451,
  DEAL_TO_TICKET: 27,
  DEAL_TO_ORDER: 511,
  EMAIL_TO_COMPANY: 186,
  EMAIL_TO_CONTACT: 198,
  EMAIL_TO_DEAL: 210,
  EMAIL_TO_PARTNER_CLIENT: 719,
  EMAIL_TO_TICKET: 224,
  EMAIL_TO_ORDER: 777,
  EMAIL_TO_CART: 779,
  EMAIL_TO_PARTNER_SERVICE: 825,
  ENGAGEMENT_TO_COMPANY: 8,
  ENGAGEMENT_TO_CONTACT: 10,
  ENGAGEMENT_TO_DEAL: 12,
  ENGAGEMENT_TO_PARTNER_CLIENT: 737,
  ENGAGEMENT_TO_ORDER: 811,
  ENGAGEMENT_TO_CART: 813,
  ENGAGEMENT_TO_TICKET: 18,
  ENGAGEMENT_TO_PARTNER_SERVICE: 827,
  MEETING_TO_CALL: 400,
  MEETING_TO_COMPANY: 188,
  MEETING_TO_CONTACT: 200,
  MEETING_TO_DEAL: 212,
  MEETING_TO_PARTNER_CLIENT: 715,
  MEETING_TO_TICKET: 226,
  MEETING_TO_ORDER: 769,
  MEETING_TO_CART: 771,
  MEETING_TO_PARTNER_SERVICE: 821,
  NOTE_TO_COMPANY: 190,
  NOTE_TO_CONTACT: 202,
  NOTE_TO_DEAL: 214,
  NOTE_TO_PARTNER_CLIENT: 713,
  NOTE_TO_TICKET: 228,
  NOTE_TO_ORDER: 765,
  NOTE_TO_CART: 767,
  NOTES_TO_PARTNER_SERVICE: 819,
  POSTAL_MAIL_TO_COMPANY: 459,
  POSTAL_MAIL_TO_CONTACT: 453,
  POSTAL_MAIL_TO_DEAL: 457,
  POSTAL_MAIL_TO_TICKET: 455,
  POSTAL_MAIL_TO_ORDER: 788,
  POSTAL_MAIL_TO_CART: 790,
  TASK_TO_COMPANY: 192,
  TASK_TO_CONTACT: 204,
  TASK_TO_DEAL: 216,
  TASK_TO_PARTNER_CLIENT: 711,
  TASK_TO_TICKET: 230,
  TASK_TO_ORDER: 727,
  TASK_TO_CART: 729,
  TASK_TO_PARTNER_SERVICE: 817,
  TICKET_TO_COMPANY_UNLABELED: 339,
  TICKET_TO_COMPANY: 26,
  TICKET_TO_CONTACT: 16,
  TICKET_TO_DEAL: 28,
  TICKET_TO_TICKET: 452,
  TICKET_TO_ORDER: 526,
  TICKET_TO_CART: 595
});

// all object type id's that are valid to associate with an engagement
const HUBSPOT_DEFINED_OBJECT_TYPE_ID_ALLOWLIST_FOR_ENGAGEMENTS = HUBSPOT_DEFINED_OBJECT_TYPE_ALLOWLIST.map(objectType => {
  return ObjectTypesToIds[objectType];
});
const MEETING_TITLE = 'hs_meeting_title';
const MEETING_START_TIME = 'hs_meeting_start_time';
const CALL_START_TIME = 'hs_timestamp';
export const NULL_LABEL_VALUE = '--';
const universalAssociationOptionComparator = memoize((optionA, optionB, includeIsSelected = true) => {
  if (includeIsSelected) {
    const aIsSelected = optionA.get('isSelected');
    const bIsSelected = optionB.get('isSelected');
    if (aIsSelected && !bIsSelected) {
      return -1;
    }
    if (!aIsSelected && bIsSelected) {
      return 1;
    }
  }
  const aPrimaryDisplayLabel = optionA.get('primaryDisplayLabel').toLowerCase();
  const bPrimaryDisplayLabel = optionB.get('primaryDisplayLabel').toLowerCase();
  if (aPrimaryDisplayLabel > bPrimaryDisplayLabel) {
    return 1;
  }
  if (aPrimaryDisplayLabel < bPrimaryDisplayLabel) {
    return -1;
  }
  const aSecondaryDisplayLabel = optionA.get('secondaryDisplayLabel');
  const bSecondaryDisplayLabel = optionB.get('secondaryDisplayLabel');

  // Secondary display labels are not guaranteed to exist, return the option without one first
  if (aSecondaryDisplayLabel && !bSecondaryDisplayLabel) {
    return 1;
  }
  if (!aSecondaryDisplayLabel && bSecondaryDisplayLabel) {
    return -1;
  }
  if (!aSecondaryDisplayLabel && !bSecondaryDisplayLabel) {
    return 0;
  }
  const lowerCaseASecondaryDisplayLabel = optionA.get('secondaryDisplayLabel').toLowerCase();
  const lowerCaseBSecondaryDisplayLabel = optionB.get('secondaryDisplayLabel').toLowerCase();
  if (lowerCaseASecondaryDisplayLabel > lowerCaseBSecondaryDisplayLabel) {
    return 1;
  }
  if (lowerCaseASecondaryDisplayLabel < lowerCaseBSecondaryDisplayLabel) {
    return -1;
  }
  return 0;
});
const creationDateGetter = value => I18n.moment.userTz(Number(value)).format('lll');
function primaryDisplayLabelGetterWithDefault(propertyName) {
  return properties => {
    const primaryDisplayLabel = getIn(['properties', propertyName, 'value'], properties);
    if (!primaryDisplayLabel && propertyName === MEETING_TITLE) {
      return I18n.text('universalAssociationsSelect.emptyDisplayLabel.loggedMeeting');
    }
    return primaryDisplayLabel || NULL_LABEL_VALUE;
  };
}
function propertyValueGetter(propertyName) {
  return getIn(['properties', propertyName, 'value']);
}
function convertToValidEngagementAssociation(definition, associationTypeIdMap) {
  const toObjectTypeId = definition.associationDefinition.toObjectTypeId;
  const associationCategory = definition.associationDefinition.associationCategory;
  if (isLegacyHubSpotObject(toObjectTypeId) && associationCategory === 'USER_DEFINED') {
    const definitionMap = fromJS(definition);
    const newAssociationTypeId = associationTypeIdMap.get(toObjectTypeId);
    return definitionMap.setIn(['associationDefinition', 'associationCategory'], 'HUBSPOT_DEFINED').setIn(['associationDefinition', 'associationTypeId'], newAssociationTypeId).setIn(['associationDefinition', 'subjectAssociationCategory'], associationCategory).toJS();
  }
  return definition;
}
function getIsValidEngagementAssociation(definition) {
  const toObjectTypeId = definition.toObjectTypeId;
  const fromObjectTypeId = definition.fromObjectTypeId;
  const associationCategory = definition.associationCategory;
  const isToCompanyContactDealOrTicket = HUBSPOT_DEFINED_OBJECT_TYPE_ID_ALLOWLIST_FOR_ENGAGEMENTS.includes(toObjectTypeId);
  const isFromCompanyContactDealOrTicket = HUBSPOT_DEFINED_OBJECT_TYPE_ID_ALLOWLIST_FOR_ENGAGEMENTS.includes(fromObjectTypeId);
  const isToCobject = isPortalSpecificObjectType(toObjectTypeId);
  const isFromCobject = isPortalSpecificObjectType(fromObjectTypeId);

  // This encompasses all flexible associations
  if (associationCategory === 'USER_DEFINED' && (isFromCompanyContactDealOrTicket || isFromCobject) && (isToCompanyContactDealOrTicket || isToCobject)) {
    return true;
  }
  if (isToCobject) {
    // all associations TO portal specific objects are valid for engagements
    // e.g. note --> vendor, task --> vendor, meeting --> vendor
    return true;
  }
  return HUBSPOT_DEFINED_ASSOCIATION_TYPE_ID_ALLOWLIST.includes(definition.associationTypeId) && associationCategory === 'HUBSPOT_DEFINED';
}
function getFormattedPrimaryAndSecondaryDisplayLabels({
  toObjectTypeId,
  primaryDisplayLabel,
  secondaryDisplayLabels,
  properties
}) {
  if (ObjectTypeFromIds[toObjectTypeId] === CONTACT) {
    const firstName = primaryDisplayLabel;
    const lastName = secondaryDisplayLabels[0];
    const email = secondaryDisplayLabels[1];
    const formattedName = formatName({
      firstName,
      lastName
    });
    const secondaryDisplayLabel = email ? I18n.text('universalAssociationsSelect.optionFormatting.emailWrapper', {
      secondaryDisplayLabel: email
    }) : undefined;
    return {
      primaryDisplayLabel: formattedName || NULL_LABEL_VALUE,
      secondaryDisplayLabel
    };
  }
  if (ObjectTypeFromIds[toObjectTypeId] === TICKET) {
    const content = properties.find(property => property.name === 'content');
    const secondaryDisplayLabel = content && content.value ? I18n.text('universalAssociationsSelect.optionFormatting.parentheses', {
      secondaryDisplayLabel: content.value
    }) : undefined;
    return {
      primaryDisplayLabel: primaryDisplayLabel || NULL_LABEL_VALUE,
      secondaryDisplayLabel
    };
  }
  if (toObjectTypeId === MEETING_EVENT_TYPE_ID) {
    const secondaryDisplayLabel = secondaryDisplayLabels[0] && creationDateGetter(Number(secondaryDisplayLabels[0]));
    return {
      primaryDisplayLabel: primaryDisplayLabel || I18n.text('universalAssociationsSelect.emptyDisplayLabel.loggedMeeting'),
      secondaryDisplayLabel: secondaryDisplayLabel ? I18n.text('universalAssociationsSelect.optionFormatting.parentheses', {
        secondaryDisplayLabel
      }) : undefined
    };
  }
  if (toObjectTypeId === CALL_TYPE_ID) {
    const callCreateDate = properties.find(property => property.name === CALL_START_TIME);
    const secondaryDisplayLabel = callCreateDate && creationDateGetter(Number(callCreateDate.value));
    return {
      primaryDisplayLabel: primaryDisplayLabel || NULL_LABEL_VALUE,
      secondaryDisplayLabel: secondaryDisplayLabel ? I18n.text('universalAssociationsSelect.optionFormatting.parentheses', {
        secondaryDisplayLabel,
        defaultValue: ''
      }) : ''
    };
  }
  const hasSecondaryDisplayLabel = ObjectTypeFromIds[toObjectTypeId] !== DEAL && secondaryDisplayLabels && !!secondaryDisplayLabels.length && !!secondaryDisplayLabels[0];
  const secondaryDisplayLabel = hasSecondaryDisplayLabel ? I18n.text('universalAssociationsSelect.optionFormatting.parentheses', {
    secondaryDisplayLabel: secondaryDisplayLabels[0]
  }) : undefined;
  return {
    primaryDisplayLabel: primaryDisplayLabel || NULL_LABEL_VALUE,
    secondaryDisplayLabel
  };
}
function getObjectDisplayLabelGettersAndObjectName({
  associationCategory,
  associationTypeId,
  pluralForm,
  singularForm,
  primaryDisplayLabelPropertyName,
  secondaryDisplayLabelPropertyNames,
  toObjectTypeId
}) {
  switch (toObjectTypeId) {
    case CONTACT_TYPE_ID:
      {
        const contactPrimaryDisplayLabelGetter = optionRecord => {
          const firstName = propertyValueGetter('firstname')(optionRecord);
          const lastName = propertyValueGetter('lastname')(optionRecord);
          return formatName({
            firstName,
            lastName
          }) || NULL_LABEL_VALUE;
        };
        return {
          associationCategory: 'HUBSPOT_DEFINED',
          associationTypeId,
          pluralObjectName: I18n.text('universalAssociationsSelect.associationType.CONTACT'),
          singularObjectName: singularForm,
          primaryDisplayLabelGetter: contactPrimaryDisplayLabelGetter,
          secondaryDisplayLabelGetter: propertyValueGetter('email'),
          toObjectType: CONTACT
        };
      }
    case COMPANY_TYPE_ID:
      return {
        associationCategory: 'HUBSPOT_DEFINED',
        associationTypeId,
        pluralObjectName: I18n.text('universalAssociationsSelect.associationType.COMPANY'),
        singularObjectName: singularForm,
        primaryDisplayLabelGetter: primaryDisplayLabelGetterWithDefault('name'),
        secondaryDisplayLabelGetter: propertyValueGetter('domain'),
        toObjectType: COMPANY
      };
    case DEAL_TYPE_ID:
      return {
        associationCategory: 'HUBSPOT_DEFINED',
        associationTypeId,
        pluralObjectName: I18n.text('universalAssociationsSelect.associationType.DEAL'),
        singularObjectName: singularForm,
        primaryDisplayLabelGetter: primaryDisplayLabelGetterWithDefault('dealname'),
        secondaryDisplayLabelGetter: undefined,
        toObjectType: DEAL
      };
    case TICKET_TYPE_ID:
      return {
        associationCategory: 'HUBSPOT_DEFINED',
        associationTypeId,
        pluralObjectName: I18n.text('universalAssociationsSelect.associationType.TICKET'),
        singularObjectName: singularForm,
        primaryDisplayLabelGetter: primaryDisplayLabelGetterWithDefault('subject'),
        secondaryDisplayLabelGetter: propertyValueGetter('content'),
        toObjectType: TICKET
      };
    case MEETING_EVENT_TYPE_ID:
      {
        return {
          associationCategory: 'HUBSPOT_DEFINED',
          associationTypeId,
          pluralObjectName: pluralForm,
          singularObjectName: singularForm,
          primaryDisplayLabelGetter: primaryDisplayLabelGetterWithDefault(primaryDisplayLabelPropertyName),
          secondaryDisplayLabelGetter: properties => {
            return creationDateGetter(propertyValueGetter(MEETING_START_TIME)(properties));
          },
          toObjectType: ENGAGEMENT
        };
      }
    case CALL_TYPE_ID:
      {
        return {
          associationCategory: 'HUBSPOT_DEFINED',
          associationTypeId,
          pluralObjectName: pluralForm,
          singularObjectName: singularForm,
          primaryDisplayLabelGetter: primaryDisplayLabelGetterWithDefault(primaryDisplayLabelPropertyName),
          secondaryDisplayLabelGetter: properties => {
            return creationDateGetter(propertyValueGetter(CALL_START_TIME)(properties));
          },
          toObjectType: ENGAGEMENT
        };
      }
    default:
      {
        // Custom object
        const secondaryDisplayLabelGetter = secondaryDisplayLabelPropertyNames && secondaryDisplayLabelPropertyNames.length ? propertyValueGetter(secondaryDisplayLabelPropertyNames[0]) : undefined;
        return {
          associationCategory,
          associationTypeId,
          pluralObjectName: pluralForm,
          singularObjectName: singularForm,
          primaryDisplayLabelGetter: primaryDisplayLabelGetterWithDefault(primaryDisplayLabelPropertyName),
          secondaryDisplayLabelGetter
        };
      }
  }
}
const getUniversalAssociationRecord = memoize(({
  associationCategory: associationDefinitionCategory,
  associationTypeId: associationDefinitionTypeId,
  toObjectTypeDefinition: {
    primaryDisplayLabelPropertyName,
    secondaryDisplayLabelPropertyNames,
    pluralForm,
    singularForm
  } = {},
  toObjectTypeId
}) => {
  const {
    associationCategory,
    associationTypeId,
    pluralObjectName,
    singularObjectName,
    primaryDisplayLabelGetter,
    secondaryDisplayLabelGetter,
    toObjectType
  } = getObjectDisplayLabelGettersAndObjectName({
    toObjectTypeId,
    primaryDisplayLabelPropertyName,
    secondaryDisplayLabelPropertyNames,
    pluralForm,
    singularForm,
    associationCategory: associationDefinitionCategory,
    associationTypeId: associationDefinitionTypeId
  });
  return UniversalAssociationRecord({
    associationCategory,
    associationTypeId,
    toObjectTypeId,
    singularObjectName,
    pluralObjectName,
    primaryDisplayLabelGetter,
    secondaryDisplayLabelGetter,
    toObjectType
  });
});
function getSubjectAssociationRecord(crmObject, subjectObjectId, subjectObjectTypeId, isExistingEngagement) {
  // We only build the subject association if the crmObject is the subject
  if (crmObject.id !== +subjectObjectId) {
    return ImmutableMap();
  }
  const subjectRecord = getUniversalAssociationRecord({
    toObjectTypeDefinition: crmObject.objectTypeDefinition,
    toObjectTypeId: subjectObjectTypeId
  });
  const {
    primaryDisplayLabel,
    secondaryDisplayLabel
  } = getFormattedPrimaryAndSecondaryDisplayLabels({
    toObjectTypeId: subjectObjectTypeId,
    primaryDisplayLabel: crmObject.primaryDisplayLabel,
    secondaryDisplayLabels: crmObject.secondaryDisplayLabels,
    properties: crmObject.properties
  });

  // Whether or not the subject is selected is only relevant when we are evaluating
  // a new engagement. Otherwise that information is pulled from the engagement itself.
  const subjectOption = OrderedMap({
    [subjectObjectId]: UniversalAssociationOptionRecord({
      isDefaultAssociation: true,
      isSelected: !isExistingEngagement,
      objectId: +subjectObjectId,
      primaryDisplayLabel,
      secondaryDisplayLabel,
      currentUserCanCommunicate: crmObject.userPermissions ? crmObject.userPermissions.currentUserCanCommunicate : undefined
    })
  });
  return ImmutableMap({
    [subjectObjectTypeId]: subjectRecord.set('associationOptionRecords', subjectOption)
  });
}
const getShouldPreselectDefaultAssociationOnLegacyHubspotObject = ({
  isPrimary,
  isSubjectCrmObject,
  engagementId,
  preselectedDefaultDealCount,
  properties,
  subjectAssociationCategory,
  subjectObjectTypeId,
  toObjectTypeId,
  autoAssociationsData
}) => {
  if (!isSubjectCrmObject) {
    return false;
  }
  if (autoAssociationsData) {
    return autoAssociationsData.autoAssociations[toObjectTypeId].some(autoAssociation => autoAssociation.objectId === engagementId && autoAssociation.canUserView);
  }
  const isSubjectLegacyHubspotObject = [CONTACT_TYPE_ID, COMPANY_TYPE_ID, DEAL_TYPE_ID, TICKET_TYPE_ID].includes(subjectObjectTypeId);

  // Below is a link to the spread sheet where we define our expected behaviors for default associations and preselecting.
  // There are no general rules for contacts or tickets that can be applied here.
  // https://docs.google.com/spreadsheets/d/1iUzusStqf2u8c7BG9Yre76AfZKRsv9t4UCsxNlZTwqU/edit#gid=913464275
  const isToObjectTypeIdContactOrTicket = [CONTACT_TYPE_ID, TICKET_TYPE_ID].includes(toObjectTypeId);
  if (!isSubjectLegacyHubspotObject || isToObjectTypeIdContactOrTicket) {
    return false;
  }

  // Ensures that only primary companies are selected by default
  // and not flexible associated companies.
  if (toObjectTypeId === COMPANY_TYPE_ID && subjectAssociationCategory !== 'USER_DEFINED' && isPrimary) {
    return true;
  }
  if (subjectObjectTypeId === TICKET_TYPE_ID) {
    return false;
  }
  const isDealOpen = properties && properties.find(property => property.name === 'hs_is_closed' && property.value === 'false');
  return !!isDealOpen && preselectedDefaultDealCount < 5;
};
const getIsDefaultAssociationOptionSelected = ({
  crmObjectId,
  isExistingEngagement,
  isPrimary,
  node,
  preselectedDefaultDealCount,
  subjectAssociationCategory,
  subjectObjectId,
  subjectObjectTypeId,
  toObjectTypeId,
  autoAssociationsData
}) => {
  if (isExistingEngagement || node.userPermissions && !node.userPermissions.currentUserCanCommunicate) {
    return false;
  }
  const isSubjectCrmObject = crmObjectId === +subjectObjectId;
  const shouldPreselectOnLegacyHubSpotObjects = getShouldPreselectDefaultAssociationOnLegacyHubspotObject({
    isPrimary,
    isSubjectCrmObject,
    engagementId: node.id,
    subjectObjectTypeId,
    toObjectTypeId,
    properties: node.properties,
    preselectedDefaultDealCount,
    subjectAssociationCategory,
    autoAssociationsData
  });
  return shouldPreselectOnLegacyHubSpotObjects;
};
const getShouldHideOption = ({
  node
}) => !!node.userPermissions && !node.userPermissions.currentUserCanView;
const getUniversalAssociationOptionRecord = ({
  toObjectTypeId,
  node,
  isDefaultAssociation,
  isSelected
}) => {
  const {
    primaryDisplayLabel,
    secondaryDisplayLabel
  } = getFormattedPrimaryAndSecondaryDisplayLabels({
    toObjectTypeId,
    primaryDisplayLabel: node.primaryDisplayLabel,
    secondaryDisplayLabels: node.secondaryDisplayLabels,
    properties: node.properties
  });
  return UniversalAssociationOptionRecord({
    isDefaultAssociation,
    isSelected,
    objectId: node.id,
    primaryDisplayLabel,
    secondaryDisplayLabel,
    currentUserCanCommunicate: node.userPermissions ? node.userPermissions.currentUserCanCommunicate : undefined
  });
};

// Format an association record for engagement associations
// (aka associations that are already selected)
const getUniversalAssociationRecordWithSelectedOptions = memoize(({
  associatedObjects,
  associationRecord
}) => {
  if (!associatedObjects) {
    return associationRecord;
  }
  const {
    edges
  } = associatedObjects;
  const toObjectTypeId = associationRecord.get('toObjectTypeId');
  const options = edges.reduce((acc, {
    node
  }) => {
    // skip any association options the user does not have permission to view
    if (getShouldHideOption({
      node
    })) {
      return acc;
    }
    const optionRecord = getUniversalAssociationOptionRecord({
      toObjectTypeId,
      node,
      isSelected: true,
      isDefaultAssociation: false
    });
    const id = `${node.id}`;
    return acc.set(id, optionRecord);
  }, OrderedMap());
  const isSelectedAToZSortedOptions = options.sort(universalAssociationOptionComparator);
  return associationRecord.set('associationOptionRecords', isSelectedAToZSortedOptions);
});

// Format an association record for all other associations
const getUniversalAssociationRecordWithAllOptions = memoize(({
  associatedObjects,
  associationRecord,
  crmObjectId,
  engagementAssociationRecords,
  isExistingEngagement,
  isPrimary,
  subjectAssociationCategory,
  subjectObjectId,
  subjectObjectTypeId,
  autoAssociationsData
}) => {
  if (!associatedObjects) {
    return associationRecord;
  }
  const {
    edges
  } = associatedObjects;
  const toObjectTypeId = associationRecord.get('toObjectTypeId');
  // We will need this to mark previously selected associations as default associations.
  let engagementAssociationRecordsToReturn = engagementAssociationRecords;
  // Helps determine whether deal options should be preselected when creating an engagement.
  let preselectedDefaultDealCount = 0;

  // default options are the objects associated with the engagement's subject,
  // but are not necessarily selected
  const defaultOptions = edges.reduce((acc, {
    node
  }) => {
    // Skip any association options the user does not have permission to view
    if (getShouldHideOption({
      node
    })) {
      return acc;
    }

    // If the option is already available in engagementAssociationRecordsToReturn,
    // that means it's already been selected and that an UniversalAssociationOptionRecord
    // already exists for it. We do not want to generate a duplicate
    // UniversalAssociationOptionRecord, so we skip it and mark the existing
    // UniversalAssociationOptionRecord as a default association.
    const pathToOption = [toObjectTypeId, 'associationOptionRecords', `${node.id}`];
    const isAlreadyAssociated = engagementAssociationRecordsToReturn.getIn([...pathToOption, 'isSelected']);
    if (isAlreadyAssociated) {
      engagementAssociationRecordsToReturn = engagementAssociationRecordsToReturn.setIn([...pathToOption, 'isDefaultAssociation'], true);
      if (toObjectTypeId === DEAL_TYPE_ID) preselectedDefaultDealCount++;
      return acc;
    }

    // We only evaluate whether or not the default association
    // should be preselected when creating a new engagement.
    const isSelected = getIsDefaultAssociationOptionSelected({
      crmObjectId,
      isExistingEngagement,
      isPrimary,
      node,
      preselectedDefaultDealCount,
      subjectAssociationCategory,
      subjectObjectId,
      subjectObjectTypeId,
      toObjectTypeId,
      autoAssociationsData
    });
    if (isSelected && toObjectTypeId === DEAL_TYPE_ID) {
      preselectedDefaultDealCount++;
    }
    const optionRecord = getUniversalAssociationOptionRecord({
      toObjectTypeId,
      node,
      isSelected,
      isDefaultAssociation: true
    });
    const id = `${node.id}`;
    return acc.set(id, optionRecord);
  }, OrderedMap());
  const selectedAssociationOptionRecords = engagementAssociationRecordsToReturn.getIn([toObjectTypeId, 'associationOptionRecords']);
  const existingDefaultAssociationOptionRecords = associationRecord.get('associationOptionRecords');

  // We want to combine and sort the selected engagement associations and the
  // default associations.
  const sortedOptions = defaultOptions.mergeDeepWith((oldValue, newValue) => newValue || oldValue, existingDefaultAssociationOptionRecords, selectedAssociationOptionRecords).sort(universalAssociationOptionComparator);
  return associationRecord.set('associationOptionRecords', sortedOptions);
});
const getRecordsFromAssociationDefinitions = memoize(allAssociationTypesFromObjectType => {
  if (!allAssociationTypesFromObjectType) {
    return ImmutableMap();
  }
  const validAssociationDefinitions = allAssociationTypesFromObjectType.filter(getIsValidEngagementAssociation);
  return ImmutableMap(validAssociationDefinitions.map(associationTypeFromObject => {
    const associationRecord = getUniversalAssociationRecord(associationTypeFromObject);
    return [associationRecord.get('toObjectTypeId'), associationRecord];
  }));
});

// Retrieves and parses the association records that are already selected/associated
const getEngagementAssociationRecords = memoize(crmObject => {
  if (!crmObject || !crmObject.allAssociations) {
    return ImmutableMap();
  }
  const validAssociations = crmObject.allAssociations.filter(({
    associationDefinition
  }) => getIsValidEngagementAssociation(associationDefinition));
  return ImmutableMap(validAssociations.map(({
    associationDefinition,
    associatedObjects
  }) => {
    const associationRecord = getUniversalAssociationRecord(associationDefinition);
    const associationRecordWithOptions = getUniversalAssociationRecordWithSelectedOptions({
      associatedObjects,
      associationRecord
    });
    return [associationRecordWithOptions.get('toObjectTypeId'), associationRecordWithOptions];
  }));
});

// Retrieves/parses all association records
const getAllAssociationRecords = memoize((crmObject, subjectObjectId, subjectObjectTypeId, isExistingEngagement, associationRecordsFromDefinitions, engagementAssociationRecords, associationTypeIdMap = ImmutableMap(), autoAssociationsData) => {
  // no subject is passed in for tasks, for example, so we just
  // want to return existing associations or the associations from definitions
  if (!crmObject) {
    if (associationRecordsFromDefinitions) {
      return associationRecordsFromDefinitions.mergeDeepWith((oldValue, newValue) => newValue || oldValue, engagementAssociationRecords);
    }
    if (engagementAssociationRecords) return engagementAssociationRecords;
    return ImmutableMap();
  }
  const validAssociations = crmObject.allAssociations.filter(({
    associationDefinition
  }) => getIsValidEngagementAssociation(associationDefinition));
  const formattedValidAssociations = validAssociations.map(definition => convertToValidEngagementAssociation(definition, associationTypeIdMap));

  // Subject association isSelected if !isExistingEngagement
  const subjectAssociation = getSubjectAssociationRecord(crmObject, subjectObjectId, subjectObjectTypeId, isExistingEngagement);
  const associations = formattedValidAssociations.reduce((acc, {
    associationDefinition,
    associatedObjects
  }) => {
    const toObjectTypeId = associationDefinition.toObjectTypeId;
    const existingRecord = acc.get(toObjectTypeId);
    const associationRecord = existingRecord || getUniversalAssociationRecord(associationDefinition);
    const associationRecordWithOptions = getUniversalAssociationRecordWithAllOptions({
      associatedObjects,
      associationRecord,
      crmObjectId: crmObject.id,
      engagementAssociationRecords,
      isExistingEngagement,
      isPrimary: associationDefinition.isPrimary,
      subjectAssociationCategory: associationDefinition.subjectAssociationCategory,
      subjectObjectId,
      subjectObjectTypeId,
      toObjectTypeId,
      autoAssociationsData
    });
    return acc.set(toObjectTypeId, associationRecordWithOptions);
  }, ImmutableMap());
  return associations
  // Merging these records provides more complete
  // association data.
  .mergeDeepWith((oldValue, newValue) => newValue || oldValue, associationRecordsFromDefinitions, engagementAssociationRecords, subjectAssociation);
});
const getParserData = ({
  allAssociationTypesFromObjectType,
  engagement
}) => {
  // If an engagement exists, it is not currently being created in the communicator
  const isExistingEngagement = !!engagement;

  // creates a map of all possible association type ids for this engagement
  // organized as { [fromObjectTypeId]: { [toObjectTypeId]: ### } }.
  // This is needed to convert flexible associations to engagement associations.
  const associationTypeIdMap = engagement && engagement.allAssociations ? engagement.allAssociations.reduce((acc, {
    associationDefinition
  }) => {
    const {
      fromObjectTypeId,
      toObjectTypeId,
      associationTypeId
    } = associationDefinition;
    return acc.setIn([fromObjectTypeId, toObjectTypeId], associationTypeId);
  }, ImmutableMap()) : ImmutableMap();
  const associationRecordsFromDefinitions = getRecordsFromAssociationDefinitions(allAssociationTypesFromObjectType);

  // The records already associated with the given engagement.
  // These are the records with associations already "checked"
  const engagementAssociationRecords = getEngagementAssociationRecords(engagement);
  return {
    isExistingEngagement,
    associationTypeIdMap,
    associationRecordsFromDefinitions,
    engagementAssociationRecords
  };
};
const getAllAutoAssociations = (allAssociations, autoAssociationsData) => {
  // Generates and appends options for each auto-association that we don't have associations data for
  // to ensure that they are included even if we failed to fetch their data
  return allAssociations.reduce((acc, associationRecord) => {
    const toObjectTypeId = associationRecord.get('toObjectTypeId');
    const autoAssociations = autoAssociationsData ? autoAssociationsData.autoAssociations[toObjectTypeId] : null;
    if (autoAssociations) {
      const associationOptionRecords = associationRecord.get('associationOptionRecords');
      const autoAssociationOptionRecords = autoAssociations.reduce((autoAcc, autoAssociation) => {
        const associationOptionRecord = associationOptionRecords.get(`${autoAssociation.objectId}`);
        if (!associationOptionRecord && autoAssociation.hydratedAutoAssociatedObject) {
          return autoAcc.set(`${autoAssociation.objectId}`, getUniversalAssociationOptionRecord({
            toObjectTypeId,
            node: {
              id: autoAssociation.objectId,
              primaryDisplayLabel: autoAssociation.hydratedAutoAssociatedObject.primaryDisplayLabel,
              secondaryDisplayLabels: autoAssociation.hydratedAutoAssociatedObject.secondaryDisplayLabels,
              properties: autoAssociation.hydratedAutoAssociatedObject.properties,
              userPermissions: {
                currentUserCanCommunicate: autoAssociation.currentUserCanCommunicate
              }
            },
            isSelected: autoAssociation.canUserView,
            isDefaultAssociation: true
          }));
        }
        return autoAcc;
      }, associationOptionRecords);
      return acc.setIn([toObjectTypeId, 'associationOptionRecords'], associationOptionRecords.mergeDeepWith((oldValue, newValue) => newValue || oldValue, autoAssociationOptionRecords));
    }
    return acc.set(toObjectTypeId, associationRecord);
  }, allAssociations);
};
function parseUniversalEngagementAssociations({
  data: {
    allAssociationTypesFromObjectType,
    engagement,
    subject
  },
  variables: {
    subjectObjectId,
    subjectObjectTypeId
  }
}) {
  const {
    isExistingEngagement,
    associationTypeIdMap,
    associationRecordsFromDefinitions,
    engagementAssociationRecords
  } = getParserData({
    allAssociationTypesFromObjectType,
    engagement
  });

  // The combined result of the engagement associations,
  // the default associations, and the association records
  // from definitions.
  const allAssociations = getAllAssociationRecords(subject, subjectObjectId, subjectObjectTypeId, isExistingEngagement, associationRecordsFromDefinitions, engagementAssociationRecords, associationTypeIdMap);
  return sortBy(association => association.get('pluralObjectName'), allAssociations);
}

// [LEGACY] Revamped version of parseUniversalEngagementAssociations that fetches associations and auto-associations data
// and parses associations to be used in the UniversalAssociationsSelect component
const useParseAutoAssociatedEngagementAssociations = ({
  engagementOrInteractionType,
  subjectObjectId,
  subjectObjectTypeId,
  shouldPreselectConfiguredAutoAssociations = false
}) => {
  const [associationsData, setAssociationsData] = useState(null);
  const [autoAssociationsData, setAutoAssociationsData] = useState(null);
  const associationsCachedData = associationsCache.get('associationsData');
  const autoAssociationsCachedData = associationsCache.get('autoAssociationsData');
  const {
    data: fetchedAssociationsData
  } = useFetchUniversalEngagementAssociations({
    engagementOrInteractionType,
    fetchPolicyOverride: 'cache-and-network',
    objectTypeId: subjectObjectTypeId,
    subjectId: subjectObjectId,
    skip: !subjectObjectTypeId || !subjectObjectId || !engagementOrInteractionType || associationsCachedData || associationsData
  });
  const allAssociationTypesFromObjectType = associationsData ? associationsData.allAssociationTypesFromObjectType : null;
  const engagement = associationsData ? associationsData.engagement : null;
  const subject = associationsData ? associationsData.subject : null;
  const {
    isExistingEngagement,
    associationTypeIdMap,
    associationRecordsFromDefinitions,
    engagementAssociationRecords
  } = getParserData({
    allAssociationTypesFromObjectType,
    engagement
  });
  const {
    data: fetchedAutoAssociationsData
  } = useFetchAutoAssociations({
    subjectObjectTypeId,
    subjectObjectId,
    engagementType: engagementOrInteractionType,
    shouldFetch: shouldPreselectConfiguredAutoAssociations && !autoAssociationsCachedData && !autoAssociationsData
  });
  useEffect(() => {
    if (fetchedAssociationsData && !associationsCachedData) {
      associationsCache.set('associationsData', fetchedAssociationsData);
      setAssociationsData(fetchedAssociationsData);
    }
  }, [associationsCachedData, fetchedAssociationsData]);
  useEffect(() => {
    if (fetchedAutoAssociationsData && !autoAssociationsCachedData) {
      associationsCache.set('autoAssociationsData', fetchedAutoAssociationsData.autoAssociationsData);
      setAutoAssociationsData(fetchedAutoAssociationsData.autoAssociationsData);
    }
  }, [autoAssociationsCachedData, fetchedAutoAssociationsData]);

  // Handles when hook gets rerun and we have cached data
  useEffect(() => {
    if (associationsCachedData && !associationsData) {
      setAssociationsData(associationsCachedData);
    }
    if (autoAssociationsCachedData && !autoAssociationsData) {
      setAutoAssociationsData(autoAssociationsCachedData);
    }
  }, [associationsCachedData, associationsData, autoAssociationsCachedData, autoAssociationsData]);
  if (!shouldPreselectConfiguredAutoAssociations) {
    return null;
  } else if (!associationsData || !autoAssociationsData) {
    // Because we don't send host apps an explicit loading state,
    // we return an empty map here to indicate that we are still loading
    return OrderedMap();
  }

  // The combined result of the engagement associations,
  // the default associations, and the association records
  // from definitions.
  const allAssociations = getAllAssociationRecords(subject, subjectObjectId, subjectObjectTypeId, isExistingEngagement, associationRecordsFromDefinitions, engagementAssociationRecords, associationTypeIdMap, autoAssociationsData);
  const allAutoAssociations = getAllAutoAssociations(allAssociations, autoAssociationsData);
  return sortBy(association => association.get('pluralObjectName'), allAutoAssociations);
};

// New version of useParseAutoAssociatedEngagementAssociations
const useParseAutoAssociations = ({
  engagementOrInteractionType,
  subjectObjectId,
  subjectObjectTypeId,
  shouldPreselectConfiguredAutoAssociations = false
}) => {
  const [associationsData, setAssociationsData] = useState(null);
  const [autoAssociationsData, setAutoAssociationsData] = useState(null);
  const associationsCachedData = associationsCache.get('associationsData');
  const autoAssociationsCachedData = associationsCache.get('autoAssociationsData');
  const {
    data: fetchedAssociationsData,
    loading: associationsLoading,
    error: associationsError
  } = useFetchUniversalEngagementAssociations({
    engagementOrInteractionType,
    fetchPolicyOverride: 'cache-and-network',
    objectTypeId: subjectObjectTypeId,
    subjectId: subjectObjectId,
    skip: !subjectObjectTypeId || !subjectObjectId || !engagementOrInteractionType || associationsCachedData || associationsData
  });
  const clearAllCachedValues = useCallback(() => {
    clearAssociationsCache();
    setAssociationsData(null);
    setAutoAssociationsData(null);
  }, []);
  const allAssociationTypesFromObjectType = associationsData ? associationsData.allAssociationTypesFromObjectType : null;
  const engagement = associationsData ? associationsData.engagement : null;
  const subject = associationsData ? associationsData.subject : null;
  const {
    isExistingEngagement,
    associationTypeIdMap,
    associationRecordsFromDefinitions,
    engagementAssociationRecords
  } = getParserData({
    allAssociationTypesFromObjectType,
    engagement
  });
  const {
    data: fetchedAutoAssociationsData,
    loading: autoAssociationsLoading,
    error: autoAssociationsError
  } = useFetchAutoAssociations({
    subjectObjectTypeId,
    subjectObjectId,
    engagementType: engagementOrInteractionType,
    shouldFetch: shouldPreselectConfiguredAutoAssociations && !autoAssociationsCachedData && !autoAssociationsData
  });
  useEffect(() => {
    if (fetchedAssociationsData && !associationsCachedData) {
      associationsCache.set('associationsData', fetchedAssociationsData);
      setAssociationsData(fetchedAssociationsData);
    }
  }, [associationsCachedData, fetchedAssociationsData]);
  useEffect(() => {
    if (fetchedAutoAssociationsData && !autoAssociationsCachedData) {
      associationsCache.set('autoAssociationsData', fetchedAutoAssociationsData.autoAssociationsData);
      setAutoAssociationsData(fetchedAutoAssociationsData.autoAssociationsData);
    }
  }, [autoAssociationsCachedData, fetchedAutoAssociationsData]);

  // Handles when hook gets rerun and we have cached data
  useEffect(() => {
    if (associationsCachedData && !associationsData) {
      setAssociationsData(associationsCachedData);
    }
    if (autoAssociationsCachedData && !autoAssociationsData) {
      setAutoAssociationsData(autoAssociationsCachedData);
    }
  }, [associationsCachedData, associationsData, autoAssociationsCachedData, autoAssociationsData]);
  if (!shouldPreselectConfiguredAutoAssociations) {
    return {
      parsedAutoAssociatedRecords: OrderedMap(),
      loading: false,
      error: false,
      clearAllCachedValues
    };
  }

  // The combined result of the engagement associations,
  // the default associations, and the association records
  // from definitions.
  const allAssociations = getAllAssociationRecords(subject, subjectObjectId, subjectObjectTypeId, isExistingEngagement, associationRecordsFromDefinitions, engagementAssociationRecords, associationTypeIdMap, autoAssociationsData);
  const allAutoAssociations = getAllAutoAssociations(allAssociations, autoAssociationsData);
  const parsedAutoAssociatedRecords = sortBy(association => association.get('pluralObjectName'), allAutoAssociations);
  const error = Boolean(associationsError || autoAssociationsError);
  const loading = !error && (Boolean(associationsLoading || autoAssociationsLoading) ||
  // handle case where data is returned from useQuery but not loaded into state
  Boolean(!associationsData || !autoAssociationsData));
  return {
    parsedAutoAssociatedRecords,
    loading,
    error,
    // So far only needed in calling-widget-ui to address the UAS never truly unmounting
    clearAllCachedValues
  };
};
export { setInAssociationsCache,
// exported for testing
clearAssociationsCache,
// used by host apps to clear cache on activity creator close
getAllAssociationRecords, getEngagementAssociationRecords, getFormattedPrimaryAndSecondaryDisplayLabels, getIsDefaultAssociationOptionSelected, getIsValidEngagementAssociation, getObjectDisplayLabelGettersAndObjectName, getRecordsFromAssociationDefinitions, getShouldHideOption, getShouldPreselectDefaultAssociationOnLegacyHubspotObject, getSubjectAssociationRecord, getUniversalAssociationOptionRecord, getUniversalAssociationRecord, getUniversalAssociationRecordWithAllOptions, getUniversalAssociationRecordWithSelectedOptions, HUBSPOT_DEFINED_OBJECT_TYPE_ALLOWLIST, parseUniversalEngagementAssociations, useParseAutoAssociatedEngagementAssociations, useParseAutoAssociations, primaryDisplayLabelGetterWithDefault, propertyValueGetter, universalAssociationOptionComparator, convertToValidEngagementAssociation };